import React, { useState, useRef } from "react";
import {
  TrendingUp,
  Upload,
  FileText,
  DollarSign,
  ClipboardList,
  AlertTriangle,
  CheckCircle,
  Download,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  Briefcase,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { MD_TABLE_COMPONENTS } from "../components/Common/MarkdownContent";
import toast from "react-hot-toast";
import api from "../api";
import { useProject } from "../contexts/ProjectContext";

// ─── Types ────────────────────────────────────────────────────────────────────

type Mode = "due-diligence" | "financial-analysis" | "ic-memo";

interface RedFlag {
  flag: string;
  description: string;
  severity: "High" | "Medium" | "Low";
}

interface AnalysisResult {
  mode: Mode;
  analysis?: string;
  memo?: string;
  redFlags?: RedFlag[];
  companyName?: string;
  parameters?: { companyName?: string; [key: string]: any };
  documentCount: number;
}

// ─── Mode Config ──────────────────────────────────────────────────────────────

const MODE_CONFIG = {
  "due-diligence": {
    icon: ClipboardList,
    label: "Due Diligence Checklist",
    description: "Comprehensive DD checklist with red flag detection across financial, commercial, legal & operational dimensions",
    color: "#6366f1",
    gradient: "from-indigo-50 to-purple-50",
  },
  "financial-analysis": {
    icon: DollarSign,
    label: "Financial Analysis",
    description: "LBO model with Base/Bull/Bear scenarios, DCF valuation, IRR/MOIC projections, and sensitivity tables",
    color: "#10b981",
    gradient: "from-emerald-50 to-teal-50",
  },
  "ic-memo": {
    icon: FileText,
    label: "IC Memo Generator",
    description: "Investment Committee memorandum — institutional quality, ready for senior decision-makers",
    color: "#f59e0b",
    gradient: "from-amber-50 to-orange-50",
  },
};

function getSeverityColor(severity: string) {
  if (severity === "High") return "text-red-700 bg-red-50 border-red-200";
  if (severity === "Medium") return "text-yellow-700 bg-yellow-50 border-yellow-200";
  return "text-green-700 bg-green-50 border-green-200";
}

// ─── Component ────────────────────────────────────────────────────────────────

const STORAGE_KEY = "investment_analysis_result";

const getSaved = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : null;
  } catch { return null; }
};

const InvestmentAnalysis: React.FC = () => {
  const { currentProject, projects } = useProject();
  const brandingProjectId = currentProject?.id ?? projects?.[0]?.id ?? null;
  const saved = getSaved();
  const [activeMode, setActiveMode] = useState<Mode>(saved?.mode || "due-diligence");
  const [result, setResult] = useState<AnalysisResult | null>(saved);
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [expandedRedFlags, setExpandedRedFlags] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [companyName, setCompanyName] = useState("");
  const [sector, setSector] = useState("");
  const [dealSize, setDealSize] = useState("");
  const [revenue, setRevenue] = useState("");
  const [ebitda, setEbitda] = useState("");
  const [revenueGrowth, setRevenueGrowth] = useState("");
  const [entryMultiple, setEntryMultiple] = useState("");
  const [exitMultiple, setExitMultiple] = useState("");
  const [holdPeriod, setHoldPeriod] = useState("5");
  const [debtEquityRatio, setDebtEquityRatio] = useState("60/40");
  const [investmentThesis, setInvestmentThesis] = useState("");
  const [keyRisks, setKeyRisks] = useState("");

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)].slice(0, 10));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)].slice(0, 10));
    }
  };

  const handleAnalyze = async () => {
    if (!companyName.trim()) { toast.error("Please enter a company name"); return; }
    setLoading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("companyName", companyName);
      formData.append("sector", sector);
      formData.append("dealSize", dealSize);
      if (activeMode === "financial-analysis") {
        formData.append("revenue", revenue);
        formData.append("ebitda", ebitda);
        formData.append("revenueGrowth", revenueGrowth);
        formData.append("entryMultiple", entryMultiple);
        formData.append("exitMultiple", exitMultiple);
        formData.append("holdPeriod", holdPeriod);
        formData.append("debtEquityRatio", debtEquityRatio);
      }
      if (activeMode === "ic-memo") {
        formData.append("investmentThesis", investmentThesis);
        formData.append("keyRisks", keyRisks);
      }
      files.forEach(f => formData.append("files", f));

      const { data } = await api.post(
        `/api/investment-analysis/${activeMode}`,
        formData
      );
      if (data.success) {
        const newResult = { ...data.data, mode: activeMode };
        setResult(newResult);
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(newResult)); } catch {}
        toast.success("Analysis complete");
      } else {
        toast.error(data.error || "Analysis failed");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type: "pdf" | "docx" | "pptx") => {
    if (!result) return;
    try {
      const content = result.memo || result.analysis || "";
      const companyName = result.companyName || result.parameters?.companyName || "analysis";
      const resp = await api.post(
        `/api/investment-analysis/export-${type}`,
        { content, title: MODE_CONFIG[result.mode].label, companyName, filename: `${companyName.replace(/\s+/g, "_")}_${result.mode}`, projectId: currentProject?.id },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${companyName}_${result.mode}.${type}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${type.toUpperCase()} downloaded`);
    } catch {
      toast.error("Export failed");
    }
  };

  const modeConfig = MODE_CONFIG[activeMode];
  const ModeIcon = modeConfig.icon;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-2 mb-1">
        <Briefcase className="w-5 h-5 text-electric-blue" />
        <h1 className="text-3xl font-bold text-super-dark-purple">PE / Investment Analysis</h1>
      </div>
      <p className="text-gray-500 text-sm -mt-4">
        Due diligence checklists, LBO financial modeling, and IC memo generation powered by AI.
      </p>

      {/* Mode Selection Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {(Object.keys(MODE_CONFIG) as Mode[]).map(mode => {
          const cfg = MODE_CONFIG[mode];
          const Icon = cfg.icon;
          const isSelected = activeMode === mode;
          return (
            <button
              key={mode}
              onClick={() => { setActiveMode(mode); setResult(null); }}
              className={`relative flex flex-col items-start p-4 rounded-xl border-2 transition-all text-left hover:shadow-md w-full ${
                isSelected
                  ? `border-electric-blue shadow-md bg-gradient-to-br ${cfg.gradient}`
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center mb-3"
                style={{ backgroundColor: cfg.color + "18" }}
              >
                <Icon style={{ color: cfg.color, width: 18, height: 18 }} />
              </div>
              <p className={`text-sm font-semibold mb-0.5 ${isSelected ? "text-super-dark-purple" : "text-gray-800"}`}>
                {cfg.label}
              </p>
              <p className="text-xs text-gray-500 leading-snug">{cfg.description}</p>
              {isSelected && (
                <div className="absolute bottom-3 right-3 w-2 h-2 rounded-full" style={{ backgroundColor: cfg.color }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* ── Left: Form ── */}
        <div className="xl:col-span-1 space-y-4">

          {/* Deal Parameters */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Deal Parameters</p>

            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Company Name *</label>
              <input
                type="text"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
                placeholder="e.g. Acme Corp"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue/20"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Sector</label>
              <input
                type="text"
                value={sector}
                onChange={e => setSector(e.target.value)}
                placeholder="e.g. B2B SaaS, Healthcare IT"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue/20"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">Deal Size</label>
              <input
                type="text"
                value={dealSize}
                onChange={e => setDealSize(e.target.value)}
                placeholder="e.g. $250M"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-electric-blue focus:ring-1 focus:ring-electric-blue/20"
              />
            </div>

            {/* Financial Analysis extras */}
            {activeMode === "financial-analysis" && (
              <div className="border-t border-gray-100 pt-3 space-y-3">
                <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">LBO Parameters</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: "Revenue (LTM)", val: revenue, set: setRevenue, ph: "$50M" },
                    { label: "EBITDA (LTM)", val: ebitda, set: setEbitda, ph: "$15M" },
                    { label: "Revenue Growth %", val: revenueGrowth, set: setRevenueGrowth, ph: "20" },
                    { label: "Hold Period (yrs)", val: holdPeriod, set: setHoldPeriod, ph: "5" },
                    { label: "Entry Multiple", val: entryMultiple, set: setEntryMultiple, ph: "10x" },
                    { label: "Exit Multiple", val: exitMultiple, set: setExitMultiple, ph: "12x" },
                  ].map(({ label, val, set, ph }) => (
                    <div key={label}>
                      <label className="text-xs text-gray-500 mb-1 block">{label}</label>
                      <input
                        type="text"
                        value={val}
                        onChange={e => set(e.target.value)}
                        placeholder={ph}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-electric-blue"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Debt/Equity Split</label>
                  <input
                    type="text"
                    value={debtEquityRatio}
                    onChange={e => setDebtEquityRatio(e.target.value)}
                    placeholder="60/40"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-electric-blue"
                  />
                </div>
              </div>
            )}

            {/* IC Memo extras */}
            {activeMode === "ic-memo" && (
              <div className="border-t border-gray-100 pt-3 space-y-3">
                <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider">IC Context (optional)</p>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Investment Thesis</label>
                  <textarea
                    value={investmentThesis}
                    onChange={e => setInvestmentThesis(e.target.value)}
                    placeholder="e.g. Market leader in fragmented niche with high switching costs..."
                    rows={3}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-electric-blue resize-none"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Key Risks</label>
                  <textarea
                    value={keyRisks}
                    onChange={e => setKeyRisks(e.target.value)}
                    placeholder="e.g. Customer concentration, competitive pressure..."
                    rows={2}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-electric-blue resize-none"
                  />
                </div>
              </div>
            )}
          </div>

          {/* File Upload */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Documents (optional)</p>
            <div
              onDragOver={e => e.preventDefault()}
              onDrop={handleFileDrop}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center cursor-pointer hover:border-electric-blue/50 transition-colors"
            >
              <Upload className="w-5 h-5 text-gray-400 mx-auto mb-2" />
              <p className="text-xs text-gray-500">Drop PDFs, 10-Ks, CIMs, financials</p>
              <p className="text-xs text-gray-400 mt-0.5">Up to 10 files · 50MB each</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.doc,.txt,.csv,.xlsx,.xls"
              onChange={handleFileSelect}
              className="hidden"
            />
            {files.length > 0 && (
              <div className="mt-3 space-y-1">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-3 h-3 text-gray-400 flex-shrink-0" />
                      <span className="text-xs text-gray-600 truncate">{f.name}</span>
                    </div>
                    <button onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} className="text-gray-400 hover:text-red-500 ml-2">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Run Button */}
          <button
            onClick={handleAnalyze}
            disabled={loading || !companyName.trim()}
            className="w-full py-3 rounded-xl font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
            style={{ backgroundColor: loading ? "#9ca3af" : modeConfig.color }}
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 animate-spin" />Analyzing...</>
            ) : (
              <><ModeIcon className="w-4 h-4" />Run {modeConfig.label}</>
            )}
          </button>
        </div>

        {/* ── Right: Results ── */}
        <div className="xl:col-span-2">
          {/* Empty state */}
          {!result && !loading && (
            <div className="h-full min-h-[300px] bg-white border-2 border-dashed border-gray-200 rounded-xl flex flex-col items-center justify-center gap-3 text-center p-8">
              <div
                className="w-16 h-16 rounded-2xl flex items-center justify-center"
                style={{ backgroundColor: modeConfig.color + "15" }}
              >
                <ModeIcon style={{ color: modeConfig.color, width: 28, height: 28 }} />
              </div>
              <div>
                <p className="text-base font-bold text-super-dark-purple mb-1">{modeConfig.label}</p>
                <p className="text-sm text-gray-500 max-w-xs leading-relaxed">{modeConfig.description}</p>
              </div>
              <div className="flex items-center gap-4 mt-2">
                {activeMode === "due-diligence" && ["Financial DD", "Commercial DD", "Red Flags"].map(t => (
                  <div key={t} className="flex items-center gap-1.5 text-xs text-gray-400">
                    <CheckCircle className="w-3.5 h-3.5 text-indigo-400" />{t}
                  </div>
                ))}
                {activeMode === "financial-analysis" && ["LBO Model", "DCF Valuation", "3 Scenarios"].map(t => (
                  <div key={t} className="flex items-center gap-1.5 text-xs text-gray-400">
                    <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />{t}
                  </div>
                ))}
                {activeMode === "ic-memo" && ["IC-Ready Format", "Full Memo", "Exportable"].map(t => (
                  <div key={t} className="flex items-center gap-1.5 text-xs text-gray-400">
                    <FileText className="w-3.5 h-3.5 text-amber-400" />{t}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col items-center justify-center py-20 gap-4">
              <div className="relative">
                <div className="w-14 h-14 rounded-full border-4 border-gray-100 border-t-electric-blue animate-spin" />
                <ModeIcon style={{ color: modeConfig.color, width: 20, height: 20 }} className="absolute inset-0 m-auto" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-super-dark-purple">Running {modeConfig.label}</p>
                <p className="text-xs text-gray-400 mt-1">for {companyName} · this may take 30–60 seconds</p>
              </div>
            </div>
          )}

          {/* Result */}
          {result && !loading && (
            <div className="space-y-4">
              {/* Result header with export buttons */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: modeConfig.color }} />
                    <span className="text-sm font-semibold text-super-dark-purple">{result.companyName} — {MODE_CONFIG[result.mode].label}</span>
                    {result.documentCount > 0 && (
                      <span className="text-xs text-gray-500 bg-white px-2 py-0.5 rounded-full border border-gray-200">
                        {result.documentCount} doc{result.documentCount > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => handleExport("pdf")}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-gray-50 text-gray-600 rounded-lg text-xs font-medium transition-colors border border-gray-200"
                    >
                      <Download className="w-3.5 h-3.5" />PDF
                    </button>
                    <button
                      onClick={() => handleExport("docx")}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-blue-50 text-blue-700 rounded-lg text-xs font-medium transition-colors border border-blue-200"
                    >
                      <Download className="w-3.5 h-3.5" />DOCX
                    </button>
                    <button
                      onClick={() => handleExport("pptx")}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-orange-50 text-orange-700 rounded-lg text-xs font-medium transition-colors border border-orange-200"
                    >
                      <Download className="w-3.5 h-3.5" />PPTX
                    </button>
                  </div>
                </div>

                {/* Red Flags */}
                {result.redFlags && result.redFlags.length > 0 && (
                  <div className="border-b border-gray-100 px-4 py-3 bg-red-50">
                    <button
                      className="flex items-center justify-between w-full"
                      onClick={() => setExpandedRedFlags(v => !v)}
                    >
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-semibold text-red-700">
                          {result.redFlags.length} Red Flag{result.redFlags.length > 1 ? "s" : ""} Detected
                        </span>
                      </div>
                      {expandedRedFlags ? <ChevronUp className="w-4 h-4 text-red-400" /> : <ChevronDown className="w-4 h-4 text-red-400" />}
                    </button>
                    {expandedRedFlags && (
                      <div className="mt-3 space-y-2">
                        {result.redFlags.map((rf, i) => (
                          <div key={i} className={`p-3 rounded-lg border ${getSeverityColor(rf.severity)}`}>
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-bold px-1.5 py-0.5 rounded border">{rf.severity}</span>
                              <div>
                                <p className="text-xs font-semibold">{rf.flag}</p>
                                <p className="text-xs opacity-80 mt-0.5">{rf.description}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Analysis Content */}
                <div className="p-5 max-h-[600px] overflow-y-auto">
                  <div className="prose prose-sm max-w-none
                    prose-headings:text-super-dark-purple prose-headings:font-bold
                    prose-h1:text-lg prose-h2:text-base prose-h3:text-sm
                    prose-p:text-gray-700 prose-p:leading-relaxed
                    prose-strong:text-super-dark-purple prose-strong:font-semibold
                    prose-li:text-gray-700 prose-li:marker:text-indigo-500
                    prose-table:w-full prose-th:text-super-dark-purple prose-th:bg-gray-50
                    prose-td:text-gray-700 prose-tr:border-gray-200
                    prose-blockquote:border-l-indigo-400 prose-blockquote:text-gray-500
                    prose-code:text-indigo-600 prose-code:bg-indigo-50 prose-code:px-1 prose-code:rounded
                  ">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_TABLE_COMPONENTS}>
                      {result.memo || result.analysis || ""}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvestmentAnalysis;
